// Разбор и форматирование момента в формате этикетки — «ДД.ММ.ГГГГ ЧЧ:ММ».
//
// Отдельным модулем от диалога: функции нужны и тестам, и другим экранам, а файл,
// экспортирующий и компонент, и утилиты, ломает fast refresh — правка утилиты
// перезагружает дерево целиком.
//
// Время человек вводит в зоне ТОЧКИ, а не устройства: «своя дата окончания» уходит
// на пищевую этикетку и в мониторинг, и разбор по часам моноблока (который вполне
// может стоять на другой зоне) поставил бы на стикер не тот час. Канон:
// `printjob-lifecycle.md`, «Расчёт срока — КАНОН» — «рендер в Location.timezone».

import { fromZonedParts, zonedParts } from '@/shared/printing';

const MOMENT_PATTERN = /^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})$/;

/** Момент в «ДД.ММ.ГГГГ ЧЧ:ММ», в зоне точки. Собирается по частям, а не через toLocaleString. */
export function formatMoment(date: Date, timeZone: string): string {
  const parts = zonedParts(date, timeZone);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${pad(parts.day)}.${pad(parts.month)}.${parts.year} ${pad(parts.hour)}:${pad(
    parts.minute,
  )}`;
}

/**
 * Разбор «ДД.ММ.ГГГГ ЧЧ:ММ» как местного времени зоны точки.
 * `null`, если строка не по формату ИЛИ такого момента в зоне нет.
 *
 * Проверка существования обязательна: «31.02.2026» без неё уехало бы на этикетку
 * мартовской датой вместо отказа. Круговая сверка идёт В ТОЙ ЖЕ ЗОНЕ — ловит и
 * несуществующий день, и (в зонах с переводом часов) пропущенный при переходе час.
 */
export function parseMoment(input: string, timeZone: string): Date | null {
  const match = MOMENT_PATTERN.exec(input.trim());
  if (!match) {
    return null;
  }

  // Через индексы, а не деструктуризацией с map: под `noUncheckedIndexedAccess`
  // элементы приходят как `number | undefined`, и приведение через `!` спрятало бы
  // ровно ту проверку, ради которой функция и написана.
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);

  const instant = fromZonedParts({ year, month, day, hour, minute, second: 0 }, timeZone);

  const back = zonedParts(instant, timeZone);
  const roundTrips =
    back.year === year &&
    back.month === month &&
    back.day === day &&
    back.hour === hour &&
    back.minute === minute;

  return roundTrips ? instant : null;
}

/** Сколько цифр несёт полный момент: ДДММГГГГЧЧММ. */
const MOMENT_DIGITS = 12;

/**
 * Привести ввод к маске «ДД.ММ.ГГГГ ЧЧ:ММ», оставив только цифры.
 *
 * Зачем маска. Поле было обычным текстовым: принимало любой текст и сообщало о проблеме
 * только при отправке. На кухне это худший момент для отказа — человек уже нажал
 * «Напечатать», держит продукт в руках и получает ошибку формата вместо этикетки.
 * Маска переносит правило в момент ввода: набрать неверную форму просто нельзя.
 *
 * Формат один и тот же на всех концах, и это не совпадение: `parseMoment` разбирает
 * ровно его, `formatMoment` ровно его собирает, а на сервер уезжает ISO — то есть
 * локальная форма существует только для человека и обязана однозначно в ISO
 * переводиться. Маска это и гарантирует.
 *
 * Разделители дописываются САМИ. Их нельзя ни набрать, ни потерять: пользователь
 * вводит двенадцать цифр, всё остальное — наше дело.
 *
 * `previous` нужен для одного случая — стирания. Backspace на разделителе убирает
 * только его, набор цифр при этом не меняется, и маска добавила бы разделитель обратно:
 * клавиша выглядела бы сломанной. Поэтому при укоротившейся строке с тем же числом
 * цифр отбрасывается последняя цифра — то есть backspace всегда что-то стирает.
 */
export function maskMoment(next: string, previous = ''): string {
  const digitsOf = (value: string): string => value.replace(/\D/g, '');

  let digits = digitsOf(next).slice(0, MOMENT_DIGITS);
  if (next.length < previous.length && digits === digitsOf(previous)) {
    digits = digits.slice(0, -1);
  }

  const parts = [
    digits.slice(0, 2), // ДД
    digits.slice(2, 4), // ММ
    digits.slice(4, 8), // ГГГГ
    digits.slice(8, 10), // ЧЧ
    digits.slice(10, 12), // ММ
  ];

  let result = parts[0] ?? '';
  if (parts[1]) {
    result += `.${parts[1]}`;
  }
  if (parts[2]) {
    result += `.${parts[2]}`;
  }
  if (parts[3]) {
    result += ` ${parts[3]}`;
  }
  if (parts[4]) {
    result += `:${parts[4]}`;
  }
  return result;
}

/**
 * Ближайшее наступление времени утра «ЧЧ:ММ» в зоне точки, строго после `now` (T-055).
 *
 * Календарь значения не имеет: вечером в 22:00 при 08:00 — завтра 08:00, после полуночи
 * в 01:00 — сегодня 08:00. Перебор трёх местных дат, а не «плюс сутки»: сутки в зоне с
 * переводом часов длятся 23 или 25 часов, и прибавка 24 ч промахнулась бы на час.
 * `null` — время не по формату.
 */
export function nextMorning(time: string, timeZone: string, now: Date): Date | null {
  if (!/^\d{2}:\d{2}$/.test(time)) return null;
  for (let offset = 0; offset <= 2; offset += 1) {
    const day = formatMoment(new Date(now.getTime() + offset * 86_400_000), timeZone).slice(0, 10);
    const at = parseMoment(`${day} ${time}`, timeZone);
    if (at !== null && at.getTime() > now.getTime()) return at;
  }
  return null;
}

/** Утро сегодня или завтра — относительно `now`, в зоне точки. Для подписи кнопки. */
export function isSameLocalDay(a: Date, b: Date, timeZone: string): boolean {
  return formatMoment(a, timeZone).slice(0, 10) === formatMoment(b, timeZone).slice(0, 10);
}
